import { Badge } from "@/core/components/ui/badge";
import { Button } from "@/core/components/ui/button";
import { ConfirmDialog } from "@/core/components/ui/confirm-dialog";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  CalendarDays,
  Check,
  Clock,
  Mail,
  MessageCircle,
  Pause,
  Pencil,
  Play,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { getReportToolLabelKey } from "../../constants/report-tools";
import { fromApiFrequency } from "../../mappers/frequency";
import { computeNextRun } from "../../mappers/next-run";
import { useScheduleTracking } from "../../tracking";
import { NotificationChannel, NotificationSchedule } from "../../types";

interface ScheduleListItemProps {
  schedule: NotificationSchedule;
  /** Referência para o cálculo do próximo envio (injetada para testabilidade). */
  now: Date;
  onEdit: (schedule: NotificationSchedule) => void;
  onToggle: (schedule: NotificationSchedule) => void;
  onDelete: (id: number) => void;
}

const CHANNEL_ICON: Record<NotificationChannel, typeof Mail> = {
  email: Mail,
  whatsapp: MessageCircle,
  push: Mail,
};

export function ScheduleListItem({
  schedule,
  now,
  onEdit,
  onToggle,
  onDelete,
}: ScheduleListItemProps) {
  const [translate] = useTranslation(["insights"]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const tracking = useScheduleTracking();

  const isActive = schedule.status === "ACTIVE";

  const { frequency, weekdays } = fromApiFrequency(schedule);

  // Este componente também renderiza dados vindos do block `schedule_list` do chat, que é
  // opaco por contrato (`z.record(z.unknown())` em `schemas/blocks.ts`) — nenhum campo é
  // validado. Um campo ausente aqui derrubaria a árvore do chat inteira, não só o card, então
  // o render tolera ausência em vez de confiar no shape.
  const sendHour = schedule.send_time?.split(":")[0];

  const summary = [
    translate(`schedule-frequency-${frequency}`),
    ...(frequency === "weekly"
      ? weekdays.map((day) => translate(`schedule-weekday-${day}`))
      : []),
    ...(sendHour
      ? [translate("schedule-time-summary", { hour: sendHour })]
      : []),
  ].join(" • ");

  const labelKey = getReportToolLabelKey(schedule.tool_key);
  // As traduções da tool são `<labelKey>-title` / `-description` (a labelKey
  // sozinha não existe no JSON). Tool desconhecida (catálogo hardcodado) —
  // mostra a própria chave.
  const title = labelKey ? translate(`${labelKey}-title`) : schedule.tool_key;

  const nextRun = computeNextRun(schedule, now);
  const nextRunLabel = nextRun
    ? format(nextRun, "dd/MM 'às' HH'h'", { locale: ptBR })
    : null;

  return (
    <div className="flex flex-col gap-3 p-5 bg-white rounded-xl border border-secondary-200">
      <div className="flex gap-2 justify-between items-start">
        <div className="flex flex-wrap gap-2 items-center">
          <h3 className="text-lg font-bold">{title}</h3>
          <Badge variant={isActive ? "successMatte" : "neutralMatte"}>
            {isActive && <Check className="mr-1 w-3 h-3" />}
            {isActive
              ? translate("schedule-status-active")
              : translate("schedule-status-paused")}
          </Badge>
        </div>

        <div className="flex gap-2 shrink-0">
          <Button
            variant="outline"
            size="icon"
            aria-label={translate("schedule-action-edit") as string}
            onClick={() => {
              tracking.editClicked(schedule.id);
              onEdit(schedule);
            }}
          >
            <Pencil className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            aria-label={
              (isActive
                ? translate("schedule-action-pause")
                : translate("schedule-action-resume")) as string
            }
            onClick={() => {
              // O botão é um toggle; `schedule_paused` só vale quando está pausando
              // (o `resumed` é Should e ficou fora desta rodada).
              if (isActive) tracking.paused(schedule.id);
              onToggle(schedule);
            }}
          >
            {isActive ? (
              <Pause className="w-4 h-4" />
            ) : (
              <Play className="w-4 h-4" />
            )}
          </Button>
          <Button
            variant="outline"
            size="icon"
            aria-label={translate("schedule-action-delete") as string}
            onClick={() => {
              tracking.deleteClicked(schedule.id);
              setConfirmOpen(true);
            }}
          >
            <Trash2 className="w-4 h-4 text-red-500" />
          </Button>
        </div>
      </div>

      <p className="flex gap-2 items-center text-sm text-neutral-500">
        <CalendarDays className="w-4 h-4 shrink-0" />
        {summary}
      </p>

      <div className="flex flex-wrap gap-2">
        {(schedule.channels ?? []).map((channel) => {
          // Canal fora do enum conhecido (backend novo, front antigo) não tem ícone:
          // cai no de e-mail em vez de renderizar `undefined` como componente.
          const Icon = CHANNEL_ICON[channel] ?? Mail;
          return (
            <span
              key={channel}
              className="inline-flex gap-1.5 items-center px-2.5 py-1 text-xs rounded-md border border-secondary-200 text-neutral-700"
            >
              <Icon className="w-3.5 h-3.5" />
              {translate(`schedule-channel-${channel}`)}
            </span>
          );
        })}
      </div>

      {nextRunLabel && (
        <>
          <div className="border-t border-secondary-200" />
          <p className="flex gap-2 items-center text-sm text-neutral-500">
            <Clock className="w-4 h-4 shrink-0" />
            <span>
              <span className="font-bold text-neutral-700">
                {translate("schedule-next-run")}:
              </span>{" "}
              {nextRunLabel}
            </span>
          </p>
        </>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        // Precisa ficar acima do Sheet que o contém (z-[99999]).
        className="z-[100001]"
        overlayClassName="z-[100000]"
        title={translate("schedule-delete-confirm-title") as string}
        description={translate("schedule-delete-confirm-description") as string}
        confirmLabel={translate("schedule-action-delete") as string}
        cancelLabel={translate("schedule-form-cancel") as string}
        onConfirm={() => {
          tracking.deleteConfirmed(schedule.id);
          onDelete(schedule.id);
        }}
      />
    </div>
  );
}
